import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { IconSize } from "~/common/components/icon/IconSize";
import { IconType } from "~/common/components/icon/IconType";
import { LinkButton } from "~/common/components/Link";
import { useSimpleMenuState, MenuBottomCornersRounded } from "~/common/components/Menu/Menu";
import { MENU_POS_RIGHT } from "~/common/components/Menu/MenuPositions";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import classes from "./ShareButton.module.less";
import { Icon } from "~/common/components/icon/Icon";
import { MenuItem } from "~/common/components/Menu/MenuItem";
import { MICROSOFT_TEAMS } from "~/utils/url/staticResources";

const COPY_TO_CLIPBOARD = {
  icon: IconType.Link,
  text: "share.copy",
};
const COPIED_TO_CLIPBOARD = {
  icon: IconType.Check,
  text: "share.copied",
};

const ShareButton = ({
  url,
  title,
  trackOnShare,
  trackOnShareWith,
}: {
  url: string;
  title?: string;
  trackOnShare: () => void;
  trackOnShareWith: (shareWith: string) => void;
}) => {
  const [isOpen, handleClick, handleClose, anchorEl, setAnchorEl] = useSimpleMenuState();
  const [clipboardMessage, setClipboardMessage] = useState(COPY_TO_CLIPBOARD);
  const isTablet = useIsMinTabletSize();
  const { t } = useTranslation();

  const subject = t("share.subject", { product: title });
  const message = `${t("media.library.preview.share.intro", {})}
  ${title}
  ${url}`;
  const messageWithoutUrl = `${t("media.library.preview.share.intro", {})}
  ${title}`;

  const shareToMSTeams = () => {
    trackOnShareWith("MS Teams");
    const URI = `MSTeams:/l/chat/0/0?users=&message=${encodeURI(message)}`;
    window.location.href = URI;
  };

  const copyToClipboard = () => {
    trackOnShareWith("Copy link");
    navigator.clipboard.writeText(url);
    setClipboardMessage(COPIED_TO_CLIPBOARD);
  };

  const divRef = useRef(null);

  const onClick = () => {
    trackOnShare();
    setAnchorEl(divRef.current);
  };

  const mobileShare = async () => {
    try {
      await navigator.share({
        title: subject,
        text: messageWithoutUrl,
        url: url,
      });
    } catch (e) {
      console.error(e);
    }
  };

  if (isTablet) {
    return (
      <div ref={divRef}>
        <LinkButton className={classes.shareBtn} onClick={onClick}>
          <Icon
            className={"icon-margin-l scha__header__language--icon"}
            type={IconType.Share}
            size={IconSize.FONT_16}
          />
          <span>{t("page.wishlist.action.share")}</span>
        </LinkButton>

        <MenuBottomCornersRounded
          anchorEl={anchorEl}
          open={isOpen}
          {...MENU_POS_RIGHT}
          onClose={() => {
            handleClose();
            setClipboardMessage(COPY_TO_CLIPBOARD);
          }}
          slotProps={{ list: { disablePadding: true } }}
        >
          <MenuItem className={classes.menuItem} onClick={shareToMSTeams}>
            <img src={MICROSOFT_TEAMS} />
            {t("share.msteams")}
          </MenuItem>
          <MenuItem className={classes.menuItem} onClick={copyToClipboard}>
            <Icon size={IconSize.FONT_20} type={clipboardMessage.icon} />
            {t(clipboardMessage.text)}
          </MenuItem>
        </MenuBottomCornersRounded>
      </div>
    );
  }

  return (
    <LinkButton className={classes.share} onClick={mobileShare}>
      <Icon className={"icon-margin-l scha__header__language--icon"} type={IconType.Share} size={IconSize.FONT_20} />
    </LinkButton>
  );
};

export default ShareButton;
